import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, BellOff, AlertTriangle, Info, Zap, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_META = {
  info:    { Icon: Info,         color: "text-sky-600",    bg: "bg-sky-50",    ring: "ring-sky-200" },
  warning: { Icon: AlertTriangle,color: "text-amber-600",  bg: "bg-amber-50",  ring: "ring-amber-200" },
  urgent:  { Icon: Zap,          color: "text-rose-600",   bg: "bg-rose-50",   ring: "ring-rose-200" },
};

const ALERTS_KEY = "schoolboss.notif.alerts_enabled";
const LAST_ALERTED_KEY = "schoolboss.notif.last_urgent_id";

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
}

// Tiny two-note chime via Web Audio (no audio file needed).
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const tones = [880, 1175]; // A5 → D6
    tones.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.4);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1200);
  } catch { /* ignore */ }
}

export default function NotificationBell() {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(
    () => (typeof window !== "undefined" && localStorage.getItem(ALERTS_KEY) === "1")
  );
  const dropdownRef = useRef(null);
  const lastUrgentIdRef = useRef(
    typeof window !== "undefined" ? localStorage.getItem(LAST_ALERTED_KEY) || "" : ""
  );
  const navigate = useNavigate();

  const fireUrgentAlert = useCallback((urgent) => {
    // 1. Soft chime
    playChime();
    // 2. In-app toast (always)
    toast.error(`Urgent: ${urgent.title}`, { description: urgent.message?.slice(0, 140) });
    // 3. Native browser notification (if granted)
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
      try {
        const n = new Notification(urgent.title || "SchoolBoss alert", {
          body: urgent.message || "",
          tag: urgent.id,
          icon: "/brand/logo-day.png",
        });
        n.onclick = () => {
          window.focus();
          if (urgent.link) navigate(urgent.link);
          else navigate("/notifications");
          n.close();
        };
      } catch { /* ignore */ }
    }
  }, [navigate]);

  const refreshCount = useCallback(async () => {
    try {
      const res = await api.get("/notifications/unread-count");
      const newCount = res.data?.count || 0;
      const urgent = res.data?.latest_urgent || null;
      setUnread(newCount);

      // Trigger alert only when alerts opted in AND it's a NEW urgent id
      if (alertsEnabled && urgent && urgent.id && urgent.id !== lastUrgentIdRef.current) {
        lastUrgentIdRef.current = urgent.id;
        try { localStorage.setItem(LAST_ALERTED_KEY, urgent.id); } catch { /* ignore */ }
        fireUrgentAlert(urgent);
      } else if (urgent && urgent.id) {
        // Keep ref in sync even when alerts are off so toggling on later doesn't replay old ones.
        lastUrgentIdRef.current = urgent.id;
        try { localStorage.setItem(LAST_ALERTED_KEY, urgent.id); } catch { /* ignore */ }
      }
    } catch { /* silently fail — polling */ }
  }, [alertsEnabled, fireUrgentAlert]);

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await api.get("/notifications", { params: { page: 1, page_size: 8 } });
      setItems(res.data?.items || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  // initial + 30s poll + immediate refresh when tab regains focus
  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, 30000);
    const onVisible = () => { if (!document.hidden) refreshCount(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshCount]);

  // close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await loadList();
  };

  const toggleAlerts = async () => {
    if (alertsEnabled) {
      setAlertsEnabled(false);
      try { localStorage.setItem(ALERTS_KEY, "0"); } catch { /* ignore */ }
      toast.success("Urgent alerts off");
      return;
    }
    // Turning ON — request browser permission if available + play a test chime so the user knows what to expect.
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission !== "granted") {
      try {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") {
          toast.info("Sound enabled. Browser notifications blocked — you can re-enable in browser settings.");
        }
      } catch { /* ignore */ }
    }
    setAlertsEnabled(true);
    try { localStorage.setItem(ALERTS_KEY, "1"); } catch { /* ignore */ }
    playChime();
    toast.success("Urgent alerts on — you'll hear a chime for new urgent items.");
  };

  const handleItemClick = async (item) => {
    if (!item.read_at) {
      try {
        await api.post(`/notifications/${item.notification_id}/read`);
        setItems((arr) => arr.map((x) => x.notification_id === item.notification_id ? { ...x, read_at: new Date().toISOString() } : x));
        setUnread((n) => Math.max(0, n - 1));
      } catch { /* ignore */ }
    }
    if (item.link) {
      setOpen(false);
      navigate(item.link);
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((arr) => arr.map((x) => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={toggle}
        className="relative rounded-full p-2 hover:bg-[#EFF6FF] transition-colors"
        aria-label="Notifications"
        data-testid="notification-bell-btn"
      >
        <Bell className="w-5 h-5 text-[#1D4ED8]" strokeWidth={1.8} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white"
            data-testid="notification-unread-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] bg-white rounded-3xl border border-[#2563EB]/10 shadow-xl overflow-hidden z-50"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <span className="font-heading font-bold text-foreground">Notifications</span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={toggleAlerts}
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold flex items-center gap-1 transition-colors ${alertsEnabled ? "bg-rose-50 text-rose-700 hover:bg-rose-100" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                title={alertsEnabled ? "Urgent alerts ON (chime + browser notification)" : "Click to enable urgent chime + browser notifications"}
                data-testid="notification-alerts-toggle"
              >
                {alertsEnabled ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
                {alertsEnabled ? "Alerts on" : "Enable alerts"}
              </button>
              <button
                type="button"
                onClick={markAll}
                disabled={unread === 0}
                className="text-xs text-[#1D4ED8] hover:underline disabled:opacity-50 disabled:no-underline px-1.5"
                data-testid="notification-mark-all-btn"
              >
                Mark all read
              </button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loading && <div className="p-6 text-center text-sm text-brand-inkMuted">Loading…</div>}
            {!loading && items.length === 0 && (
              <div className="p-6 text-center text-sm text-brand-inkMuted">You're all caught up 🎉</div>
            )}
            {!loading && items.map((it) => {
              const meta = PRIORITY_META[it.priority] || PRIORITY_META.info;
              return (
                <button
                  key={it.recipient_id}
                  type="button"
                  onClick={() => handleItemClick(it)}
                  className={`w-full text-left flex gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${it.read_at ? "opacity-70" : ""}`}
                  data-testid={`notification-item-${it.notification_id}`}
                >
                  <div className={`shrink-0 w-9 h-9 rounded-2xl ${meta.bg} ${meta.color} ring-1 ${meta.ring} flex items-center justify-center`}>
                    <meta.Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold truncate ${it.read_at ? "text-slate-600" : "text-foreground"}`}>{it.title}</span>
                      {!it.read_at && <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0" />}
                    </div>
                    <p className="text-xs text-brand-inkMuted line-clamp-2 mt-0.5">{it.message}</p>
                    <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-400">
                      <span>{timeAgo(it.created_at)}</span>
                      {it.link && <ExternalLink className="w-3 h-3" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block text-center text-sm font-semibold text-[#1D4ED8] py-3 hover:bg-[#EFF6FF] border-t border-slate-100"
            data-testid="notification-view-all-link"
          >
            View all notifications
          </Link>
        </div>
      )}
    </div>
  );
}
